import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { MobileSearchField } from '../common';
import { getPlayerNameColor } from '../../utils/playerUtils';
import {
  getRankTierBadgeClass,
  getRankTierTableRowClass,
} from '../../utils/leaderboardRankTiers';
import { mergeLocalSessionGames } from '../../utils/session/buildLocalSessionGames';
import { isBrowserOffline } from '../../utils/session/offlineQueueConstants';

/**
 * SessionRanking — leaderboard for the current session, ranked by:
 * 1. Win percentage (primary)
 * 2. Average winning margin (tie-breaker)
 * 3. Recent form
 * 4. ELO change for the session
 *
 * Loads once when the tab opens; use Refresh to pull latest game results.
 */
const SessionRanking = ({
  sessionId,
  playersById,
  selectedClub,
  season,
  sessionPlayerStatus,
  staticMode = false,
  filterPlayerIds = undefined,
  /** Competitive 1v1: show session singles Elo column instead of mixed/levels */
  useSinglesEloColumns = false,
  refreshKey = 0,
  localGames = null,
}) => {
  const [rankingData, setRankingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const localGamesRef = useRef(localGames);
  localGamesRef.current = localGames;

  // Fetch session ranking data
  const fetchSessionRanking = useCallback(async (isManualRefresh = false) => {
    const fallbackGames = Array.isArray(localGamesRef.current) ? localGamesRef.current : [];
    if (!sessionId || (!selectedClub?.id && fallbackGames.length === 0)) {
      return;
    }

    const singlesEloMode = useSinglesEloColumns;

    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      let sessionGames = fallbackGames;
      let gamesError = null;

      if (!isBrowserOffline() && sessionId) {
        const fetched = await supabase
          .from('session_games')
          .select(`
            id,
            team_1_score,
            team_2_score,
            winning_team,
            created_at,
            players_session_games (
              player_id,
              team_number,
              result,
              pair_type,
              players (
                id,
                first_name,
                last_name
              )
            )
          `)
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });
        gamesError = fetched.error;
        if (!gamesError) {
          sessionGames = mergeLocalSessionGames(fetched.data || [], fallbackGames);
        } else if (fallbackGames.length === 0) {
          throw gamesError;
        }
      }

      // Transform the data to match the expected format
      const gameHistory = [];
      if (sessionGames) {
        sessionGames.forEach(game => {
          if (game.players_session_games) {
            game.players_session_games.forEach(playerGame => {
              const player = playerGame.players;
              if (player) {
                gameHistory.push({
                  player_id: player.id,
                  player_first_name: player.first_name,
                  player_last_name: player.last_name,
                  result: playerGame.result,
                  player_score: playerGame.team_number === 1 ? game.team_1_score : game.team_2_score,
                  opponent_score: playerGame.team_number === 1 ? game.team_2_score : game.team_1_score,
                  game_id: game.id,
                  created_at: game.created_at
                });
              }
            });
          }
        });
      }

      if (gamesError) {
        console.warn('SessionRanking: Using local game history after fetch error:', gamesError);
      }

      let eloChanges = [];
      if (!isBrowserOffline() && selectedClub?.id && season?.id) {
        const { data: eloData, error: eloError } = await supabase
          .from('elo_history')
          .select(`
            player_id,
            elo_change,
            old_value,
            new_value,
            pair_type,
            game_type_id,
            game_types:game_type_id ( name ),
            created_at
          `)
          .eq('session_id', sessionId)
          .eq('club_id', selectedClub.id)
          .eq('season_id', season.id);

        if (eloError) {
          console.warn('SessionRanking: ELO changes unavailable, ranking from games only:', eloError);
        } else {
          eloChanges = eloData || [];
        }
      }

        // Group game history by player and calculate statistics
        const playerStats = {};
        
        gameHistory.forEach(game => {
        const playerId = game.player_id;
        
        if (!playerStats[playerId]) {
          playerStats[playerId] = {
            player_id: playerId,
            player_first_name: game.player_first_name,
            player_last_name: game.player_last_name,
            games: [],
            wins: 0,
            losses: 0,
            total_margins: 0
          };
        }
        
        const playerStat = playerStats[playerId];
        playerStat.games.push(game);
        
        // Count wins/losses
        if (game.result === 'win') {
          playerStat.wins++;
        } else if (game.result === 'loss') {
          playerStat.losses++;
        }
        
        // Calculate margins (only for wins)
        if (game.result === 'win' && game.player_score && game.opponent_score) {
          const margin = game.player_score - game.opponent_score;
          playerStat.total_margins += margin;
        }
      });

        // Combine data and calculate rankings
        const playerRankings = [];

        // Get only players who are in the current session
        let sessionPlayers = Object.values(playersById).filter(player => 
          sessionPlayerStatus && sessionPlayerStatus[player.id] !== undefined
        );
        
        // Apply player filter if provided
        if (filterPlayerIds && filterPlayerIds.length > 0) {
          sessionPlayers = sessionPlayers.filter(player => 
            filterPlayerIds.includes(player.id)
          );
        }

      sessionPlayers.forEach(player => {
        const playerStat = playerStats[player.id];
        const eloData = eloChanges.filter(e => e.player_id === player.id);

        // Calculate ELO changes by type and determine final in-session ELO values
        // Prefer game_type_id (1=mixed, 2=men's levels, 3=women's levels). Fallback to pair_type/name
        let mixedEloChange = 0;
        let levelsEloChange = 0;
        let mixedFinalElo = undefined;
        let levelsFinalElo = undefined;
        let singlesEloChange = 0;
        let singlesFinalElo = undefined;

        if (eloData.length > 0) {
          const isFemale = (player.gender || '').toString().toLowerCase().startsWith('f');
          const levelsTypeId = isFemale ? 3 : 2; // 2=Men, 3=Women

          if (singlesEloMode) {
            const singlesEntries = eloData.filter(
              (e) =>
                [4, 5, 7].includes(e.game_type_id) ||
                e.pair_type === 'singles' ||
                (e.game_types?.name || '').toLowerCase().includes('single')
            );
            const singlesAsc = [...singlesEntries].sort(
              (a, b) => new Date(a.created_at) - new Date(b.created_at)
            );
            const singlesDesc = [...singlesEntries].sort(
              (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            if (singlesAsc.length > 0) {
              const firstOld = singlesAsc[0].old_value ?? 0;
              const lastNew = singlesDesc[0].new_value ?? firstOld;
              singlesEloChange = lastNew - firstOld;
              singlesFinalElo = lastNew;
            }
          }

          const mixedEntries = eloData.filter(e => (
            !singlesEloMode &&
            (e.game_type_id === 1 || e.pair_type === 'mixed' || (e.game_types?.name || '').toLowerCase().includes('mixed'))
          ));
          const mixedAsc = [...mixedEntries].sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
          const mixedDesc = [...mixedEntries].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
          if (mixedAsc.length > 0) {
            const firstOld = mixedAsc[0].old_value ?? 0;
            const lastNew = mixedDesc[0].new_value ?? firstOld;
            mixedEloChange = lastNew - firstOld;
            mixedFinalElo = lastNew;
          }

          const levelsEntries = eloData.filter(e => (
            !singlesEloMode &&
            (e.game_type_id === levelsTypeId || e.pair_type === 'levels' || (e.game_types?.name || '').toLowerCase().includes('level'))
          ));
          const levelsAsc = [...levelsEntries].sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
          const levelsDesc = [...levelsEntries].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
          if (levelsAsc.length > 0) {
            const firstOld = levelsAsc[0].old_value ?? 0;
            const lastNew = levelsDesc[0].new_value ?? firstOld;
            levelsEloChange = lastNew - firstOld;
            levelsFinalElo = lastNew;
          }
        }
        // If we still couldn't compute a type, fall back to total-for-mixed as last resort (doubles only)
        if (
          !singlesEloMode &&
          mixedFinalElo === undefined &&
          levelsFinalElo === undefined &&
          eloData.length > 0
        ) {
          const sortedAll = [...eloData].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
          const totalEloChange = sortedAll.reduce((sum, change) => sum + (change.elo_change || 0), 0);
          mixedEloChange = totalEloChange;
          if (sortedAll.length > 0) mixedFinalElo = sortedAll[0].new_value;
        }

        // Get current ELO ratings
        const currentMixedElo = player.mixed_elo_rating || 1200;
        const currentLevelsElo = player.levels_elo_rating || 1200;
        const currentSinglesElo =
          player.singles_elo_rating != null && player.singles_elo_rating !== ''
            ? Number(player.singles_elo_rating)
            : 1200;
        // Prefer final ELO from session history when available; otherwise fall back to current
        const finalMixedElo = mixedFinalElo !== undefined ? mixedFinalElo : currentMixedElo;
        const finalLevelsElo = levelsFinalElo !== undefined ? levelsFinalElo : currentLevelsElo;
        const finalSinglesElo = singlesFinalElo !== undefined ? singlesFinalElo : currentSinglesElo;

        // Calculate statistics
        const gamesPlayed = playerStat ? playerStat.wins + playerStat.losses : 0;
        const winPercentage = gamesPlayed > 0 ? (playerStat.wins / gamesPlayed) * 100 : 0;
        const averageMargin = playerStat && playerStat.wins > 0 ? playerStat.total_margins / playerStat.wins : 0;
        
        // Generate recent form (last 5 games) as WLW format
        let recentForm = '';
        if (playerStat && playerStat.games.length > 0) {
          const lastGames = playerStat.games.slice(-5);
          recentForm = lastGames.map(game => game.result === 'win' ? 'W' : 'L').join('');
        }

        // Construct player name
        const playerName = playerStat ? 
          `${playerStat.player_first_name || ''} ${playerStat.player_last_name || ''}`.trim() : 
          player.name;

        const rankingEntry = {
          player_id: player.id,
          player_name: playerName,
          gender: player.gender, // Add gender for color coding
          games_played: gamesPlayed,
          wins: playerStat?.wins || 0,
          losses: playerStat?.losses || 0,
          win_percentage: winPercentage,
          average_margin: averageMargin,
          recent_form: recentForm,
          mixed_elo: finalMixedElo,
          mixed_elo_change: mixedEloChange,
          levels_elo: finalLevelsElo,
          levels_elo_change: levelsEloChange,
          singles_elo: finalSinglesElo,
          singles_elo_change: singlesEloChange,
          has_played: gamesPlayed > 0, // Track if player has played any games
          rank: 0 // Will be calculated below
        };

        playerRankings.push(rankingEntry);
      });

      // Sort by win percentage (desc), then by total margins (desc)
      // Players who haven't played yet go to the bottom
      playerRankings.sort((a, b) => {
        // If one has played and the other hasn't, prioritize the one who has played
        if (a.has_played !== b.has_played) {
          return b.has_played - a.has_played;
        }
        
        // If neither has played, sort by name
        if (!a.has_played && !b.has_played) {
          return a.player_name.localeCompare(b.player_name);
        }
        
        // If both have played, sort by win percentage, then average margins
        if (a.win_percentage !== b.win_percentage) {
          return b.win_percentage - a.win_percentage;
        }
        return b.average_margin - a.average_margin;
      });

      // Assign ranks
      playerRankings.forEach((player, index) => {
        player.rank = index + 1;
      });

      setRankingData(playerRankings);
      setLastUpdated(new Date());

    } catch (err) {
      console.error('SessionRanking: Error fetching ranking data:', err);
      if (fallbackGames.length > 0) {
        setError(null);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [sessionId, selectedClub?.id, season?.id, playersById, sessionPlayerStatus, filterPlayerIds, useSinglesEloColumns]);

  // Load once when session context is ready (tab mount). Use the Refresh button after new games.
  useEffect(() => {
    if (!sessionId) return;
    if (!selectedClub?.id && !(Array.isArray(localGames) && localGames.length > 0)) return;
    fetchSessionRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid refetch on every live roster/elo update
  }, [sessionId, selectedClub?.id, season?.id, useSinglesEloColumns, refreshKey]);

  const handleManualRefresh = () => {
    fetchSessionRanking(true);
  };

  const visibleRanking = useMemo(() => {
    const q = playerSearchQuery.trim().toLowerCase();
    if (!q) return rankingData;
    return rankingData.filter((player) =>
      (player.player_name || '').toLowerCase().includes(q)
    );
  }, [rankingData, playerSearchQuery]);

  const playerSearchActive = playerSearchQuery.trim().length > 0;

  // Format recent form for display (styled W/L indicators)
  const formatRecentForm = (form) => {
    if (!form || form.length === 0) return 'N/A';
    
    return form.split('').map((result, index) => (
      <span
        key={index}
        className={`inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full mr-1 ${
          result === 'W' 
? 'bg-success-light text-success-dark border border-success'
            : 'bg-danger-light text-danger-dark border border-danger'
        }`}
      >
        {result}
      </span>
    ));
  };

  // Get trend indicator
  const getTrendIndicator = (eloChange) => {
    if (eloChange > 0) return '📈';
    if (eloChange < 0) return '📉';
    return '➡️';
  };

  if (loading) {
    return (
      <div className="mt-6 p-8 text-center">
        <div className="text-2xl mb-4">⏳</div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">Loading Rankings...</h2>
        <p className="text-text-secondary">Fetching session data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 p-8 text-center">
        <div className="text-2xl mb-4">❌</div>
        <h2 className="text-xl font-semibold text-danger-dark mb-2">Error Loading Rankings</h2>
        <p className="text-text-secondary">{error}</p>
        <button 
          onClick={fetchSessionRanking}
          className="mt-4 px-4 py-2 bg-primary text-text-inverse rounded hover:bg-primary-dark"
        >
          Retry
        </button>
      </div>
    );
  }

  if (rankingData.length === 0) {
    return (
      <div className="mt-6 p-8 text-center">
        <div className="text-2xl mb-4">🏆</div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">No Games Played Yet</h2>
        <p className="text-text-secondary">Rankings will appear once games are completed.</p>
      </div>
    );
  }

  const rankBadge = (rank) => {
    const badgeClass = getRankTierBadgeClass(rank, 'full');
    if (badgeClass) {
      return (
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold ${badgeClass}`}
          aria-label={`Rank ${rank}`}
        >
          {rank}
        </span>
      );
    }
    return <span className="w-7 text-center text-sm font-semibold text-text-secondary">#{rank}</span>;
  };

  const eloChangeText = (change) => {
    if (change === 0) return <span className="text-text-muted">—</span>;
    return (
      <span className={change > 0 ? 'text-success-dark' : 'text-danger-dark'}>
        {change > 0 ? '+' : ''}{change}
      </span>
    );
  };

  const winPctColor = (pct, played) =>
    played === 0 ? 'text-text-muted'
    : pct >= 70 ? 'text-success-dark'
    : pct >= 50 ? 'text-primary'
    : 'text-text-secondary';

  const header = (
    <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-border-medium bg-surface-subtle">
      <div>
        <h2 className="text-lg font-semibold text-text-primary sm:text-xl">Session Leaderboard</h2>
        <p className="text-xs text-text-secondary mt-0.5 sm:text-sm">
          Ranked by win % • {playerSearchActive
            ? `${visibleRanking.length} of ${rankingData.length} players`
            : `${rankingData.length} players`}
        </p>
      </div>
      {!staticMode && (
        <button
          onClick={handleManualRefresh}
          disabled={isRefreshing}
          className={`flex items-center gap-1.5 rounded-md border border-border-medium px-2.5 py-1.5 text-sm font-medium transition-colors ${
            isRefreshing
              ? 'cursor-not-allowed bg-surface-subtle text-text-muted'
              : 'bg-surface text-text-primary hover:bg-surface-subtle'
          }`}
          aria-label="Refresh rankings"
        >
          <svg className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="hidden sm:inline">{isRefreshing ? 'Refreshing…' : 'Refresh'}</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="mt-6">
      <div className="rounded-lg border border-border-medium bg-surface shadow-sm overflow-hidden">
        {header}

        <div className="border-b border-border-medium px-4 py-3 sm:px-6">
          <MobileSearchField
            id="session-ranking-player-search"
            aria-label="Search rankings by player name"
            value={playerSearchQuery}
            onChange={(e) => setPlayerSearchQuery(e.target.value)}
            placeholder="Search players…"
            className="form-input w-full min-w-0 rounded-md border-border-medium bg-field px-2.5 py-1.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary sm:max-w-sm"
          />
        </div>

        {visibleRanking.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-text-secondary sm:px-6">
            No players match that name. Try another search.
          </p>
        ) : (
        <>
        {/* ── Mobile card list (hidden on md+) ─────────────────────────── */}
        <ul className="divide-y divide-border-medium md:hidden">
          {visibleRanking.map((player) => (
              <li
                key={player.player_id}
                className={`flex items-start gap-3 px-4 py-3 ${getRankTierTableRowClass(player.rank, 'full')}`}
              >
                {/* Rank */}
                <div className="mt-0.5 w-7 shrink-0 flex justify-center">
                  {rankBadge(player.rank)}
                </div>

                {/* Main content */}
                <div className="min-w-0 flex-1">
                  {/* Name row */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`truncate text-sm font-semibold ${getPlayerNameColor(player.gender)}`}>
                      {player.player_name}
                    </span>
                    {/* Win % */}
                    <span className={`shrink-0 text-sm font-bold tabular-nums ${winPctColor(player.win_percentage, player.games_played)}`}>
                      {player.games_played === 0 ? '—' : `${player.win_percentage.toFixed(0)}%`}
                    </span>
                  </div>

                  {/* Stats row */}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
                    {/* W-L */}
                    <span>
                      <span className="font-medium text-success-dark">{player.wins}W</span>
                      {' '}
                      <span className="font-medium text-danger-dark">{player.losses}L</span>
                    </span>

                    {/* Form */}
                    {player.recent_form && player.recent_form.length > 0 && (
                      <span className="flex items-center gap-0.5">
                        {player.recent_form.split('').map((r, i) => (
                          <span
                            key={i}
                            className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                              r === 'W'
                                ? 'bg-success-light text-success-dark'
                                : 'bg-danger-light text-danger-dark'
                            }`}
                          >
                            {r}
                          </span>
                        ))}
                      </span>
                    )}

                    {/* Elo changes */}
                    {useSinglesEloColumns ? (
                      <span className="tabular-nums">
                        Singles {eloChangeText(player.singles_elo_change)}
                      </span>
                    ) : (
                      <>
                        {player.mixed_elo_change !== 0 && (
                          <span className="tabular-nums">
                            Mix {eloChangeText(player.mixed_elo_change)}
                          </span>
                        )}
                        {player.levels_elo_change !== 0 && (
                          <span className="tabular-nums">
                            Lvl {eloChangeText(player.levels_elo_change)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </li>
          ))}
        </ul>

        {/* ── Desktop table (hidden below md) ──────────────────────────── */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-subtle">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Rank</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Player</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Games</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Win %</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Avg Margin</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Form</th>
                {useSinglesEloColumns ? (
                  <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Singles ELO</th>
                ) : (
                  <>
                    <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Mixed ELO</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">Levels ELO</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border-medium">
              {visibleRanking.map((player) => (
                <tr
                  key={player.player_id}
                  className={getRankTierTableRowClass(player.rank, 'full')}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {rankBadge(player.rank)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${getPlayerNameColor(player.gender)}`}>{player.player_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`text-sm ${player.games_played > 0 ? 'text-text-primary' : 'text-text-muted'}`}>{player.games_played}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`text-sm font-semibold ${winPctColor(player.win_percentage, player.games_played)}`}>
                      {player.games_played === 0 ? 'N/A' : `${player.win_percentage.toFixed(1)}%`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`text-sm font-medium ${
                      player.games_played === 0 || player.wins === 0 ? 'text-text-muted' :
                      player.average_margin > 0 ? 'text-success-dark' :
                      player.average_margin < 0 ? 'text-danger-dark' : 'text-text-secondary'
                    }`}>
                      {player.games_played === 0 || player.wins === 0 ? 'N/A' : `${player.average_margin > 0 ? '+' : ''}${player.average_margin.toFixed(1)}`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center">{formatRecentForm(player.recent_form)}</div>
                  </td>
                  {useSinglesEloColumns ? (
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-semibold text-text-primary">{player.singles_elo}</span>
                        <span className={`text-xs font-medium ${player.singles_elo_change > 0 ? 'text-success-dark' : player.singles_elo_change < 0 ? 'text-danger-dark' : 'text-text-muted'}`}>
                          {player.singles_elo_change > 0 ? '+' : ''}{player.singles_elo_change}
                        </span>
                      </div>
                    </td>
                  ) : (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-semibold text-text-primary">{player.mixed_elo}</span>
                          <span className={`text-xs font-medium ${player.mixed_elo_change > 0 ? 'text-success-dark' : player.mixed_elo_change < 0 ? 'text-danger-dark' : 'text-text-muted'}`}>
                            {player.mixed_elo_change > 0 ? '+' : ''}{player.mixed_elo_change}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-sm font-semibold text-text-primary">{player.levels_elo}</span>
                          <span className={`text-xs font-medium ${player.levels_elo_change > 0 ? 'text-success-dark' : player.levels_elo_change < 0 ? 'text-danger-dark' : 'text-text-muted'}`}>
                            {player.levels_elo_change > 0 ? '+' : ''}{player.levels_elo_change}
                          </span>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}

        <div className="border-t border-border-medium bg-surface-subtle px-4 py-2.5">
          <p className="text-center text-xs text-text-muted">
            {staticMode
              ? 'Ties broken by average winning margin'
              : 'Tap Refresh after new games • ties broken by average winning margin'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionRanking;
